import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { RootState } from "..";
import { IStoreItem } from "../../interfaces/IStoredItem";
import { typedAction } from "../helpers";
import { ISuggestedItems } from "../models/reduxModels";
import {
  RESET_USER_SUGGESTED_ITEMS,
  SET_USER_SUGGESTED_ITEMS,
  SET_SUGGESTED_IS_LOADING,
} from "./actionTypes";
import { StoreItemHelper } from "../../components/helpers/storeItemHelper";
import { logger } from "../../../../utilities/logger";
import LZString = require("lz-string");
import { apiGetCompressedSuggestedData, apiSetSuggestedItems } from "../../services/ApiService";

const initialState: ISuggestedItems = {
  isSuggestedItemsLoading: false,
  suggestedItems: null
};
//SUGGESTED ITEMS
//Get, Set, Add, Reset, Delete
const setIsSuggestedItemsLoading = (isSuggestedItemsLoading: boolean) => {
  return typedAction(SET_SUGGESTED_IS_LOADING, isSuggestedItemsLoading);
};

export const setSuggestedItems = (suggestedItems: IStoreItem[]) => {
  return typedAction(SET_USER_SUGGESTED_ITEMS, suggestedItems);
};

export const resetSuggestedItems = () => {
  return typedAction(RESET_USER_SUGGESTED_ITEMS);
};

export const getSuggestedItems = (accountId: string, sender: string): ThunkAction<Promise<number>, RootState, unknown, Action<string>> => async (dispatch) => {
  dispatch(setIsSuggestedItemsLoading(true));
  let suggestedItems: IStoreItem[];
  try {
    let compressedSuggestedData: string = await apiGetCompressedSuggestedData(accountId, sender);
    if (compressedSuggestedData) {
      const decompressedSuggested = LZString.decompressFromBase64(compressedSuggestedData);
      if (decompressedSuggested) {
        suggestedItems = JSON.parse(decompressedSuggested);
      }
    }
  } catch (error) {
    logger(error, "suggestedItems.ts (getSuggestedItems)");
    suggestedItems = null;
  }
  if (suggestedItems?.length > 25) {
    suggestedItems = suggestedItems.slice(0, 25);
  }

  dispatch(setSuggestedItems(suggestedItems ?? []));
  return suggestedItems ? suggestedItems.length : 0;
};

export const addSuggestedItem = (accountId: string, sender: string, itemToAdd: IStoreItem): ThunkAction<void, RootState, unknown, Action<string>> => async (dispatch, getState) => {
  if (sender && itemToAdd) {
    let updatedSuggestedItems: IStoreItem[];
    try {
      const existingSuggestedItems: IStoreItem[] = getState().suggested?.suggestedItems ?? [];

      let itemExists = false;
      updatedSuggestedItems = existingSuggestedItems.map(item => {
        if (StoreItemHelper.itemExists(item, [itemToAdd])) {
          itemExists = true;
          return { ...item, saveCount: item.saveCount ? item.saveCount + 1 : 2 };
        }
        return { ...item };
      });

      if (!itemExists) {
        updatedSuggestedItems.unshift({ ...itemToAdd, saveCount: 1 });
      }

      updatedSuggestedItems.sort((a, b) => b.saveCount - a.saveCount);

      apiSetSuggestedItems(accountId, sender, updatedSuggestedItems);
      dispatch(setSuggestedItems(updatedSuggestedItems));
    } catch (e) {
      logger(e, "suggestedItems.ts (addSuggestedItem)");
    }
  }
};

export const removeSuggestedItem = (accountId: string, sender: string, item: { itemId: string; driveId: string }): ThunkAction<void, RootState, unknown, Action<string>> => async (dispatch, getState) => {
  try {
    let updatedItems: IStoreItem[];
    const existingSuggestedItems: IStoreItem[] = getState().suggested?.suggestedItems ?? [];

    updatedItems = !item.itemId
      ? existingSuggestedItems.filter(x => x.driveId !== item.driveId || x.itemId != item.itemId)
      : existingSuggestedItems.filter(x => x.itemId !== item.itemId);

    dispatch(setSuggestedItems(updatedItems));
    apiSetSuggestedItems(accountId, sender, updatedItems);
  } catch (e) {
    logger(e, "suggestedItems.ts (removeSuggestedItem)");
  }
};

type HomeAction = ReturnType<
  | typeof setSuggestedItems
  | typeof resetSuggestedItems
  | typeof setIsSuggestedItemsLoading
>;

export function suggestedReducer(state = initialState, action: HomeAction): ISuggestedItems {
  switch (action.type) {
    case SET_SUGGESTED_IS_LOADING:
      return { ...state, isSuggestedItemsLoading: action.payload };
    case SET_USER_SUGGESTED_ITEMS:
      return { isSuggestedItemsLoading: false, suggestedItems: action.payload };
    case RESET_USER_SUGGESTED_ITEMS:
      return initialState;
    default:
      return state;
  }
}
